import { supabase } from '../config/supabaseClient';
import { API_BASE_URL } from '../config';

// Helper to construct API URLs with correct structure
const buildApiUrl = (path: string, params?: Record<string, string>): string => {
  // Ensure path starts with a slash
  const formattedPath = path.startsWith('/') ? path : `/${path}`;
  const baseUrl = (API_BASE_URL || '').trim();
  let url = `${baseUrl}/api${formattedPath}`;
  
  // Add query parameters if provided
  if (params && Object.keys(params).length > 0) {
    const queryString = Object.entries(params)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    url = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
  }
  
  return url;
};

// Helper function to get authentication token
const getToken = async () => {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || '';
};

// Helper functions for fetch API requests
const fetchJSON = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  // Ensure proper URL construction without spaces
  const cleanUrl = url.replace(/\s+/g, '');
  
  console.log('Fetching URL:', cleanUrl);
  console.log('Request options:', JSON.stringify(options, null, 2));
  
  const response = await fetch(cleanUrl, options);
  
  if (!response.ok) {
    console.error(`API Error: ${response.status} ${response.statusText}`, { url: cleanUrl });
    
    try {
      // Try to get more details about the error
      const errorText = await response.text();
      console.error('Error response text:', errorText);
    } catch (e) {
      console.error('Could not read error response text', e);
    }
    
    throw new Error(`Network response was not ok: ${response.status} ${response.statusText}`);
  }
  
  return await response.json() as T;
};

// Helper to create standard request options with authentication
const createRequestOptions = async (method: string, body?: any, contentType = 'application/json'): Promise<{options: RequestInit, queryParams?: Record<string, string>}> => {
  const token = await getToken();
  
  // Get user ID from Supabase - crucial for backend API authentication
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;
  
  if (!userId) {
    console.warn('No authenticated user found in createRequestOptions');
  }
  
  const options: RequestInit = {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
    } as HeadersInit,
  };
  
  let queryParams: Record<string, string> | undefined;
  
  // For GET requests, we need to add userId as a query parameter
  if (method === 'GET' && userId) {
    queryParams = { userId };
  }
  
  if (body) {
    if (contentType && contentType !== 'multipart/form-data') {
      (options.headers as Record<string, string>)['Content-Type'] = contentType;
    }
    
    if (contentType === 'application/json' && typeof body === 'object') {
      // Include userId in the request body
      options.body = JSON.stringify({
        ...body,
        userId: userId // This is critical for backend to identify the user
      });
    } else {
      options.body = body;
    }
  } else if (method !== 'GET' && method !== 'DELETE') {
    // For non-GET/DELETE requests without body, still include userId
    options.body = JSON.stringify({ userId });
    if (contentType === 'application/json') {
      (options.headers as Record<string, string>)['Content-Type'] = contentType;
    }
  }
  
  return { options, queryParams };
};

// Types
export interface ABTestVariant {
  name: string;
  isControl: boolean;
  subject: string;
  content: string;
  distribution: number;
  templateId?: string;
}

export interface StepVariantConfig {
  name: string;
  isControl?: boolean;
  subject?: string;
  content?: string;
  distribution?: number;
  autoWinnerThreshold?: number;
}

export type BranchEventType =
  |'send'
  |'open'
  |'click'
  |'reply'
  |'bounce'
  |'no_event';

export interface StepBranchCondition {
  event: BranchEventType;
  withinDays?: number;
}

export interface StepBranchAction {
  type: 'move_to_step' | 'exit_sequence';
  targetStepNumber?: number;
}

export interface StepBranchConfig {
  name: string;
  priority?: number;
  isDefault?: boolean;
  condition: StepBranchCondition;
  action: StepBranchAction;
}

export interface FollowUpStep {
  id?: string;
  campaign_id?: string;
  step_number: number;
  subject: string;
  template_id?: string | null;
  template_action?: 'select' | 'create';
  new_template_name?: string;
  new_template_content?: string;
  interval_days: number; // days to wait before sending this step
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  variants?: StepVariantConfig[];
  branches?: StepBranchConfig[];
}

export interface ScheduleSettings {
  enabled: boolean;
  timezone: string;
  send_days: string[]; // array of days ['monday', 'tuesday', etc]
  send_time_start: string; // HH:MM format
  send_time_end: string; // HH:MM format
  max_emails_per_day?: number;
}

export interface EmailCampaign {
  id?: string;
  name: string;
  subject: string;
  mailbox_id?: string | null;
  template_id?: string | null;
  template_action?: 'select' | 'create';
  new_template_name?: string;
  new_template_content?: string;
  status?: 'draft' | 'active' | 'paused' | 'completed';
  total_contacts?: number;
  sent_count?: number;
  opened_count?: number;
  replied_count?: number;
  bounced_count?: number;
  created_at?: string;
  updated_at?: string;
  mailbox?: EmailMailbox;
  // Multi-step sequence properties
  is_sequence?: boolean;
  follow_up_steps?: FollowUpStep[];
  schedule_settings?: ScheduleSettings;
  // A/B Testing properties
  enableABTesting?: boolean;
  abTestType?: 'subject' | 'content' | 'sender_name' | 'send_time';
  abTestVariants?: ABTestVariant[];
  winnerMetric?: 'open_rate' | 'click_rate' | 'reply_rate' | 'conversion_rate';
  userSelectedVariant?: string;
  // Attribution
  source_playbook_id?: string | null;
  source_playbook_goal?: string | null;
  source_ai_designer?: boolean;
}

export interface EmailContact {
  id?: string;
  email: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  position?: string;
  status: 'active' | 'unsubscribed' | 'bounced';
  lead_value?: number; // monetary value of the lead
  lead_status?: 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';
  lead_source?: string; // which campaign generated the lead
  conversion_date?: string; // date when the lead was converted
  notes?: string; // additional notes about the lead
}

export interface EmailMailbox {
  id?: string;
  email: string;
  name: string;
  smtp_host: string;
  smtp_port: number;
  smtp_secure?: boolean;
  smtp_username: string;
  smtp_password?: string;
  imap_host?: string;
  imap_port?: number;
  imap_username?: string;
  imap_password?: string;
  daily_limit: number;
  emails_sent_today?: number;
  status: 'active' | 'paused' | 'error';
  created_at?: string;
  last_check?: string | null;
}

export interface EmailTemplate {
  id?: string;
  name: string;
  subject: string;
  content: string;
  variables?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface CampaignTemplate {
  id: string;
  name: string;
  category: string;
  industry: string;
  use_case: string;
  subject: string;
  body: string;
  variables: string[];
  spam_score: number;
  recommended_tone: string;
  best_practices: string[];
  tags: string[];
}

export interface CampaignTemplateCategories {
  categories: string[];
  industries: string[];
  use_cases: string[];
}

export interface SendTimeRecommendation {
  best_day: string;
  best_hour: number;
  open_probability: number;
  reply_probability: number;
  confidence: number;
  data_points: number;
}

export interface SendTimeOptimizationAnalysis {
  user_heatmap: Array<{
    day: string;
    hour: number;
    open_rate: number;
    reply_rate: number;
    sends: number;
  }>;
  industry_benchmarks: Array<{
    day: string;
    hour: number;
    open_rate: number;
    reply_rate: number;
    sends: number;
  }>;
  recommendations: SendTimeRecommendation[];
  best_overall: SendTimeRecommendation;
  insights: string[];
}

export interface EmailPlaybookMeta {
  id: string;
  name: string;
  description: string;
  goal: string;
  industries: string[];
  useCases: string[];
  stepsCount: number;
}

export interface PlaybookFollowUpStepDraft {
  step_number: number;
  subject: string;
  interval_days: number;
  is_active: boolean;
}

export interface EmailPlaybookStepRef {
  stepNumber: number;
  templateId: string;
  intervalDays: number;
}

export interface EmailPlaybook {
  id: string;
  name: string;
  description: string;
  goal: string;
  industries: string[];
  useCases: string[];
  recommendedWinnerMetric?: 'open_rate' | 'click_rate' | 'reply_rate';
  steps: EmailPlaybookStepRef[];
}

export interface EmailPlaybookStatsTotals {
  total_contacts: number;
  sent: number;
  opened: number;
  replied: number;
  bounced: number;
}

export interface EmailPlaybookStats {
  campaignCount: number;
  totals: EmailPlaybookStatsTotals;
  openRate: number;
  replyRate: number;
  bounceRate: number;
}

export interface AICampaignDesignRequest {
  goal: 'pipeline' | 'demo' | 'trial' | 'nurture' | 'reactivation' | 'event' | 'referral' | 'partnership' | 'recruiting';
  industry?: string;
  targetAudience: string;
  productDescription: string;
  tone?: 'professional' | 'casual' | 'friendly' | 'direct';
  length?: 'short' | 'medium' | 'long';
  sequenceLength?: number;
  campaignName?: string;
  includeAIContent?: boolean;
}

export interface AICampaignDesignResult {
  playbookId: string;
  playbookName: string;
  campaignDraft: Partial<EmailCampaign>;
  rationale?: string;
}

export interface CampaignContact {
  id?: string;
  campaign_id: string;
  contact_id: string;
  status: 'pending' | 'sent' | 'opened' | 'replied' | 'bounced' | 'failed';
  sent_at?: string;
  opened_at?: string;
  replied_at?: string;
  bounced_at?: string;
  contact?: EmailContact;
}

// Email Warmup interfaces
interface WarmupConfig {
  increase_rate: number; // Percentage to increase daily sends
  increase_days: number; // How many days between increases
  max_daily: number; // Maximum daily emails to send
}

interface WarmupStatus {
  id: string;
  mailbox_id: string;
  user_id: string;
  is_active: boolean;
  daily_limit: number;
  current_daily_volume: number;
  warmup_started_at: string;
  last_warmup_at: string | null;
  next_scheduled_at: string | null;
  reputation_score: number;
  last_error: string | null;
  config: WarmupConfig;
  created_at: string;
  updated_at: string;
}

interface WarmupEmail {
  id: string;
  warmup_status_id: string;
  mailbox_id: string;
  user_id: string;
  from_email: string;
  to_email: string;
  subject: string;
  content: string;
  content_type: string;
  status: 'pending' | 'sent' | 'opened' | 'replied' | 'failed';
  error_message?: string;
  sent_at?: string;
  opened_at?: string;
  replied_at?: string;
  created_at: string;
  updated_at: string;
}

interface WarmupHistoryResponse {
  emails: WarmupEmail[];
  total: number;
}

interface WarmupStatistics {
  sent_count: number;
  opened_count: number;
  replied_count: number;
  failed_count: number;
  open_rate: number;
  reply_rate: number;
  daily_volume_chart: {
    date: string;
    sent: number;
  }[];
  reputation_chart: {
    date: string;
    score: number;
  }[];
}

export interface MailboxImportResult {
  success: boolean;
  total: number;
  processed: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  results: Array<{
    row: number;
    email: string;
    status: 'created' | 'updated' | 'skipped' | 'failed' | 'error';
    error?: string;
    reason?: string;
    mailbox_id?: string;
  }>;
}

// Interface for the email service
export interface EmailServiceInterface {
  // Campaigns
  getCampaigns: () => Promise<EmailCampaign[]>;
  getCampaign: (campaignId: string) => Promise<EmailCampaign>;
  createCampaign: (campaign: EmailCampaign) => Promise<EmailCampaign>;
  updateCampaign: (campaign: EmailCampaign) => Promise<EmailCampaign>;
  updateCampaignStatus: (campaignId: string, status: string) => Promise<EmailCampaign>;
  deleteCampaign: (id: string) => Promise<void>;
  getPlaybooks: () => Promise<EmailPlaybookMeta[]>;
  buildPlaybookDraft: (playbookId: string, name?: string) => Promise<{ playbook: EmailPlaybook; campaignDraft: Partial<EmailCampaign> }>;
  designAICampaign: (payload: AICampaignDesignRequest) => Promise<AICampaignDesignResult>;
  getPlaybookStats: (playbookId: string) => Promise<EmailPlaybookStats>;

  // Mailboxes
  getMailboxes: () => Promise<EmailMailbox[]>;
  createMailbox: (mailbox: EmailMailbox) => Promise<EmailMailbox>;
  updateMailbox: (id: string, mailbox: Partial<EmailMailbox>) => Promise<EmailMailbox>;
  deleteMailbox: (id: string) => Promise<void>;
  testMailboxConnection: (mailboxData: EmailMailbox) => Promise<boolean>;
  downloadMailboxTemplate: () => Promise<void>;
  importMailboxes: (file: File, options: { testConnections?: boolean; duplicateHandling?: 'skip' | 'update' }) => Promise<MailboxImportResult>;
  getEncryptionStatus: () => Promise<boolean>;

  // Contacts
  getContacts: () => Promise<EmailContact[]>;
  getContact: (id: string) => Promise<EmailContact>;
  createContact: (contact: EmailContact) => Promise<EmailContact>;
  updateContact: (id: string, contactData: Partial<EmailContact>) => Promise<EmailContact>;
  deleteContact: (id: string) => Promise<void>;
  downloadContactsTemplate: () => Promise<void>;
  importContacts: (file: File, fieldMapping: Record<string, string>, duplicateHandling: 'skip' | 'update' | 'create_new') => Promise<{ total: number, imported: number, skipped: number, updated: number }>;
  importGoogleSheetsContacts: (sheetUrl: string, sheetName: string, range: string, fieldMapping: Record<string, string>, duplicateHandling: 'skip' | 'update' | 'create_new') => Promise<{ total: number, imported: number, skipped: number, updated: number }>;
  createContacts: (contacts: EmailContact[]) => Promise<{ contacts: EmailContact[], count: number }>;

  // Templates
  getTemplates: () => Promise<EmailTemplate[]>;
  createTemplate: (template: EmailTemplate) => Promise<EmailTemplate>;
  deleteTemplate: (id: string) => Promise<void>;
  getCampaignTemplates: () => Promise<CampaignTemplate[]>;
  getCampaignTemplateCategories: () => Promise<CampaignTemplateCategories>;
  getSendTimeRecommendations: (timezone: string) => Promise<SendTimeOptimizationAnalysis>;

  // Campaign Contacts
  getCampaignContacts: (campaignId: string) => Promise<CampaignContact[]>;
  addContactsToCampaign: (campaignId: string, contactIds: string[]) => Promise<{ campaignContacts: CampaignContact[], count: number }>;

  // Dashboard metrics
  getDashboardMetrics: () => Promise<{
    totalSentToday: number;
    dailyLimit: number;
    activeCampaigns: number;
    openRate: number;
    replyRate: number;
    bounceRate: number;
    totalLeads: number;
    leadsToday: number;
    leadConversionRate: number;
    averageLeadValue: number;
    totalLeadValue: number;
    positiveResponses: number;
    positiveResponsesToday: number;
    positiveResponseRate: number;
  }>;
  getDailyActivity: () => Promise<{ date: string; sent: number }[]>;
  getRecentActivity: () => Promise<{
    id: string;
    type: string;
    email: string;
    campaign: string;
    timestamp: number;
  }[]>;

  // Unified inbox methods
  getUnifiedThreads: (filter?: 'inbox' | 'starred' | 'archived', page?: number, pageSize?: number) => Promise<UnifiedThreadsResponse>;
  getUnifiedThreadMessages: (threadId: string) => Promise<UnifiedThreadMessagesResponse>;
  markUnifiedMessageRead: (messageId: string, isRead: boolean) => Promise<{ success: boolean }>;
  starUnifiedMessage: (messageId: string, isStarred: boolean) => Promise<{ success: boolean }>;
  archiveUnifiedMessage: (messageId: string, isArchived: boolean) => Promise<{ success: boolean }>;
  markAsPositiveResponse: (messageId: string, isPositive: boolean, reason?: string) => Promise<{ success: boolean }>;
  sendUnifiedReply: (messageId: string, content: string, isHtml?: boolean) => Promise<{ success: boolean }>;
  syncUnifiedMailbox: (mailboxId: string) => Promise<{ success: boolean; messageCount: number }>;
  // Unified inbox polish
  snoozeUnifiedMessage: (messageId: string, untilIso: string) => Promise<{ success: boolean }>;
  assignUnifiedMessage: (messageId: string, assignee: string) => Promise<{ success: boolean }>;

  // Email warmup methods
  getMailboxWarmupStatus: (mailboxId: string) => Promise<WarmupStatus | null>;
  enableWarmup: (mailboxId: string, config?: Partial<WarmupConfig>) => Promise<WarmupStatus>;
  disableWarmup: (mailboxId: string) => Promise<WarmupStatus>;
  getWarmupHistory: (mailboxId: string, page?: number, pageSize?: number) => Promise<WarmupHistoryResponse>;
  getWarmupStatistics: (mailboxId: string) => Promise<WarmupStatistics>;
}

// Unified Inbox
export interface UnifiedThread {
  id: string;
  thread_id: string;
  subject?: string;
  snippet?: string;
  participants: {
    email: string;
    name?: string;
  }[];
  message_count: number;
  has_unread: boolean;
  last_message_at: string;
}

export interface UnifiedMessage {
  id: string;
  mailbox_id: string;
  message_id: string;
  thread_id: string;
  from_address: string;
  from_name?: string;
  to_addresses: string[];
  subject?: string;
  body_text?: string;
  body_html?: string;
  is_read: boolean;
  is_starred: boolean;
  is_archived: boolean;
  is_replied: boolean;
  intent?: string;
  snooze_until?: string | null;
  assignee?: string | null;
  is_positive_response?: boolean;
  positive_response_reason?: string;
  received_at: string;
}

export interface UnifiedThreadsResponse {
  threads: UnifiedThread[];
  total: number;
}

export interface UnifiedThreadMessagesResponse {
  messages: UnifiedMessage[];
}

// Define unified inbox methods
const unifiedInboxMethods = {
  // Get unified threads (conversations)
  getUnifiedThreads: async (
    filter: 'inbox' | 'starred' | 'archived' = 'inbox',
    page: number = 1,
    pageSize: number = 20
  ): Promise<UnifiedThreadsResponse> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl(
      '/unified-inbox/threads',
      {
        ...(queryParams || {}),
        filter,
        page: page.toString(),
        pageSize: pageSize.toString(),
      }
    );
    
    return fetchJSON<UnifiedThreadsResponse>(url, options);
  },

  // Mailbox security / encryption status
  getEncryptionStatus: async (): Promise<boolean> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl('/email/security/encryption-status', queryParams);
    const response = await fetchJSON<{ encryptionConfigured: boolean }>(url, options);
    return !!response.encryptionConfigured;
  },
  
  // Get messages in a thread
  getUnifiedThreadMessages: async (threadId: string): Promise<UnifiedThreadMessagesResponse> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl(`/unified-inbox/threads/${threadId}`, queryParams);
    
    return fetchJSON<UnifiedThreadMessagesResponse>(url, options);
  },
  
  // Mark message as read/unread
  markUnifiedMessageRead: async (messageId: string, isRead: boolean): Promise<{ success: boolean }> => {
    const { options } = await createRequestOptions('PATCH', { isRead });
    const url = buildApiUrl(`/unified-inbox/messages/${messageId}/read`);
    
    return fetchJSON<{ success: boolean }>(url, options);
  },
  
  // Star/unstar message
  starUnifiedMessage: async (messageId: string, isStarred: boolean): Promise<{ success: boolean }> => {
    const { options } = await createRequestOptions('PATCH', { isStarred });
    const url = buildApiUrl(`/unified-inbox/messages/${messageId}/star`);
    
    return fetchJSON<{ success: boolean }>(url, options);
  },
  
  // Archive/unarchive message
  archiveUnifiedMessage: async (messageId: string, isArchived: boolean): Promise<{ success: boolean }> => {
    const { options } = await createRequestOptions('PATCH', { isArchived });
    const url = buildApiUrl(`/unified-inbox/messages/${messageId}/archive`);
    
    return fetchJSON<{ success: boolean }>(url, options);
  },
  
  // Send reply to a message
  sendUnifiedReply: async (messageId: string, content: string, isHtml: boolean = true): Promise<{ success: boolean }> => {
    const { options } = await createRequestOptions('POST', { content, isHtml });
    const url = buildApiUrl(`/unified-inbox/messages/${messageId}/reply`);
    
    return fetchJSON<{ success: boolean }>(url, options);
  },
  
  // Trigger manual sync of a mailbox
  syncUnifiedMailbox: async (mailboxId: string): Promise<{ success: boolean; messageCount: number }> => {
    const { options } = await createRequestOptions('POST');
    const url = buildApiUrl(`/unified-inbox/sync/${mailboxId}`);
    
    return fetchJSON<{ success: boolean; messageCount: number }>(url, options);
  },
  
  // Mark message as positive response or not
  markAsPositiveResponse: async (
    messageId: string, 
    isPositive: boolean, 
    reason?: string
  ): Promise<{ success: boolean }> => {
    return createRequestOptions('POST').then(({ options }) => {
      options.body = JSON.stringify({
        is_positive_response: isPositive,
        reason: reason || ''
      });
      return fetchJSON<{ success: boolean }>(
        buildApiUrl(`/unified-inbox/messages/${messageId}/positive-response`),
        options
      );
    });
  },
  
  // Snooze a message until a future datetime (ISO)
  snoozeUnifiedMessage: async (messageId: string, untilIso: string): Promise<{ success: boolean }> => {
    const { options } = await createRequestOptions('PATCH', { until: untilIso });
    const url = buildApiUrl(`/unified-inbox/messages/${messageId}/snooze`);
    return fetchJSON<{ success: boolean }>(url, options);
  },

  // Assign a message to a user/assignee identifier
  assignUnifiedMessage: async (messageId: string, assignee: string): Promise<{ success: boolean }> => {
    const { options } = await createRequestOptions('PATCH', { assignee });
    const url = buildApiUrl(`/unified-inbox/messages/${messageId}/assign`);
    return fetchJSON<{ success: boolean }>(url, options);
  },
};

// Create the complete email service by combining the base methods and unified inbox methods
const baseEmailService = {
  getMailboxes: async (): Promise<EmailMailbox[]> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl('/email/mailboxes', queryParams);
    const response = await fetchJSON<{ mailboxes: EmailMailbox[] }>(url, options);
    return response.mailboxes || [];
  },
  getDashboardMetrics: async (): Promise<{
    totalSentToday: number;
    dailyLimit: number;
    activeCampaigns: number;
    openRate: number;
    replyRate: number;
    bounceRate: number;
    totalLeads: number;
    leadsToday: number;
    leadConversionRate: number;
    averageLeadValue: number;
    totalLeadValue: number;
    positiveResponses: number;
    positiveResponsesToday: number;
    positiveResponseRate: number;
  }> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl('/email/dashboard/metrics', queryParams);
    return fetchJSON<{
      totalSentToday: number;
      dailyLimit: number;
      activeCampaigns: number;
      openRate: number;
      replyRate: number;
      bounceRate: number;
      totalLeads: number;
      leadsToday: number;
      leadConversionRate: number;
      averageLeadValue: number;
      totalLeadValue: number;
      positiveResponses: number;
      positiveResponsesToday: number;
      positiveResponseRate: number;
    }>(url, options);
  },
  getDailyActivity: async (): Promise<{ date: string; sent: number }[]> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl('/email/dashboard/activity', queryParams);
    return fetchJSON<{ date: string; sent: number }[]>(url, options);
  },
  getRecentActivity: async (): Promise<{
    id: string;
    type: string;
    email: string;
    campaign: string;
    timestamp: number;
  }[]> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl('/email/dashboard/recent', queryParams);
    return fetchJSON<{
      id: string;
      type: string;
      email: string;
      campaign: string;
      timestamp: number;
    }[]>(url, options);
  },
};

const emailService = {
  ...baseEmailService,
  ...unifiedInboxMethods,
  
  // Direct send via connected Gmail/SMTP
  sendEmail: async (params: { to: string; subject?: string; text?: string; html?: string; fromName?: string }): Promise<{ success: boolean; id?: string }> => {
    const { to } = params || ({} as any);
    if (!to || typeof to !== 'string') {
      throw new Error("'to' is required and must be a string");
    }
    if (!params.text && !params.html) {
      throw new Error("Either 'text' or 'html' must be provided");
    }
    const { options } = await createRequestOptions('POST', {
      to: params.to,
      subject: params.subject,
      text: params.text,
      html: params.html,
      fromName: params.fromName,
    });
    const url = buildApiUrl('/email/send');
    return fetchJSON<{ success: boolean; id?: string }>(url, options);
  },
  
  // Campaigns
  getCampaigns: async (): Promise<EmailCampaign[]> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl('/email/campaigns', queryParams);
    const response = await fetchJSON<{ campaigns: EmailCampaign[] }>(url, options);
    return response.campaigns || [];
  },
  getCampaign: async (campaignId: string): Promise<EmailCampaign> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl(`/email/campaigns/${campaignId}`, queryParams);
    return fetchJSON<{ campaign: EmailCampaign }>(url, options).then(res => res.campaign);
  },
  createCampaign: async (campaign: EmailCampaign): Promise<EmailCampaign> => {
    const { options } = await createRequestOptions('POST', campaign);
    const url = buildApiUrl('/email/campaigns');
    const response = await fetchJSON<{ campaign: EmailCampaign }>(url, options);
    return response.campaign;
  },
  updateCampaign: async (campaign: EmailCampaign): Promise<EmailCampaign> => {
    if (!campaign.id) throw new Error('Campaign ID is required');
    const { options } = await createRequestOptions('PATCH', campaign);
    const url = buildApiUrl(`/email/campaigns/${campaign.id}`);
    const response = await fetchJSON<{ campaign: EmailCampaign }>(url, options);
    return response.campaign;
  },
  updateCampaignMailboxAssignment: async (
    campaignId: string,
    assignmentId: string,
    payload: { status?: 'active' | 'paused' | 'exhausted'; daily_cap?: number | null; priority?: number; weight?: number }
  ): Promise<{ assignment: any }> => {
    const { options } = await createRequestOptions('PATCH', payload);
    const url = buildApiUrl(`/email/campaigns/${campaignId}/mailboxes/${assignmentId}`);
    return fetchJSON<{ assignment: any }>(url, options);
  },
  updateCampaignStatus: async (campaignId: string, status: string): Promise<EmailCampaign> => {
    const { options } = await createRequestOptions('PATCH', { status });
    const url = buildApiUrl(`/email/campaigns/${campaignId}/status`);
    const response = await fetchJSON<{ campaign: EmailCampaign }>(url, options);
    return response.campaign;
  },
  deleteCampaign: async (id: string): Promise<void> => {
    const { options } = await createRequestOptions('DELETE');
    const url = buildApiUrl(`/email/campaigns/${id}`);
    await fetchJSON<void>(url, options);
  },
  getCampaignContacts: async (campaignId: string): Promise<CampaignContact[]> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl(`/email/campaigns/${campaignId}/contacts`, queryParams);
    const response = await fetchJSON<{ campaignContacts: CampaignContact[] }>(url, options);
    return response.campaignContacts || [];
  },
  addContactsToCampaign: async (campaignId: string, contactIds: string[]): Promise<{ campaignContacts: CampaignContact[]; count: number }> => {
    const { options } = await createRequestOptions('POST', { contact_ids: contactIds });
    const url = buildApiUrl(`/email/campaigns/${campaignId}/contacts`);
    return fetchJSON<{ campaignContacts: CampaignContact[]; count: number }>(url, options);
  },

  // Playbooks & AI campaign designer
  getPlaybooks: async (): Promise<EmailPlaybookMeta[]> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl('/email/playbooks', queryParams);
    const response = await fetchJSON<{ success: boolean; data: { playbooks: EmailPlaybookMeta[] } }>(url, options);
    return response.data.playbooks || [];
  },

  buildPlaybookDraft: async (
    playbookId: string,
    name?: string
  ): Promise<{ playbook: EmailPlaybook; campaignDraft: Partial<EmailCampaign> }> => {
    const body: any = {};
    if (name) {
      body.name = name;
    }
    const { options } = await createRequestOptions('POST', body);
    const url = buildApiUrl(`/email/playbooks/${playbookId}/draft`);
    const response = await fetchJSON<{
      success: boolean;
      data: { playbook: EmailPlaybook; campaignDraft: Partial<EmailCampaign> };
    }>(url, options);
    return response.data;
  },

  getPlaybookStats: async (playbookId: string): Promise<EmailPlaybookStats> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl(`/email/playbooks/${playbookId}/stats`, queryParams);
    const response = await fetchJSON<{ success: boolean; data: EmailPlaybookStats }>(url, options);
    return response.data;
  },

  designAICampaign: async (payload: AICampaignDesignRequest): Promise<AICampaignDesignResult> => {
    const { options } = await createRequestOptions('POST', payload);
    const url = buildApiUrl('/email/campaigns/ai-designer');
    const response = await fetchJSON<{ success: boolean; data: AICampaignDesignResult }>(url, options);
    return response.data;
  },

  // Templates
  getTemplates: async (): Promise<EmailTemplate[]> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl('/email/templates', queryParams);
    const response = await fetchJSON<{ templates: EmailTemplate[] }>(url, options);
    return response.templates || [];
  },
  createTemplate: async (template: EmailTemplate): Promise<EmailTemplate> => {
    const { options } = await createRequestOptions('POST', template);
    const url = buildApiUrl('/email/templates');
    const response = await fetchJSON<{ template: EmailTemplate }>(url, options);
    return response.template;
  },
  deleteTemplate: async (id: string): Promise<void> => {
    const { options } = await createRequestOptions('DELETE');
    const url = buildApiUrl(`/email/templates/${id}`);
    await fetchJSON<void>(url, options);
  },
  getCampaignTemplates: async (): Promise<CampaignTemplate[]> => {
    const url = buildApiUrl('/email/campaign-templates');
    const response = await fetchJSON<{ success: boolean; data?: { templates?: CampaignTemplate[] } }>(url);
    return response.data?.templates || [];
  },
  getCampaignTemplateCategories: async (): Promise<CampaignTemplateCategories> => {
    const url = buildApiUrl('/email/campaign-templates/categories');
    const response = await fetchJSON<{ success: boolean; data?: CampaignTemplateCategories }>(url);
    return response.data || { categories: [], industries: [], use_cases: [] };
  },
  getSendTimeRecommendations: async (timezone: string): Promise<SendTimeOptimizationAnalysis> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl('/email/send-time-optimization/recommendations', {
      ...(queryParams || {}),
      timezone
    });
    const response = await fetchJSON<{ success: boolean; data: SendTimeOptimizationAnalysis }>(url, options);
    return response.data;
  },

  // Contacts
  getContacts: async (): Promise<EmailContact[]> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl('/email/contacts', queryParams);
    const response = await fetchJSON<{ contacts: EmailContact[] }>(url, options);
    return response.contacts || [];
  },
  getContact: async (id: string): Promise<EmailContact> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const url = buildApiUrl(`/email/contacts/${id}`, queryParams);
    return fetchJSON<{ contact: EmailContact }>(url, options).then(res => res.contact);
  },
  createContact: async (contact: EmailContact): Promise<EmailContact> => {
    const { options } = await createRequestOptions('POST', contact);
    const url = buildApiUrl('/email/contacts');
    const response = await fetchJSON<{ contact: EmailContact }>(url, options);
    return response.contact;
  },
  updateContact: async (id: string, contactData: Partial<EmailContact>): Promise<EmailContact> => {
    const { options } = await createRequestOptions('PATCH', contactData);
    const url = buildApiUrl(`/email/contacts/${id}`);
    const response = await fetchJSON<{ contact: EmailContact }>(url, options);
    return response.contact;
  },
  deleteContact: async (id: string): Promise<void> => {
    const { options } = await createRequestOptions('DELETE');
    const url = buildApiUrl(`/email/contacts/${id}`);
    await fetchJSON<void>(url, options);
  },
  createContacts: async (contacts: EmailContact[]): Promise<{ contacts: EmailContact[]; count: number }> => {
    const { options } = await createRequestOptions('POST', { contacts });
    const url = buildApiUrl('/email/contacts/bulk');
    return fetchJSON<{ contacts: EmailContact[]; count: number }>(url, options);
  },
  downloadContactsTemplate: async (): Promise<void> => {
    const token = await getToken();
    const url = buildApiUrl('/email/contacts/template');

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to download template');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = 'contact_import_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
  },
  importContacts: async (
    file: File,
    fieldMapping: Record<string, string>,
    duplicateHandling: 'skip' | 'update' | 'create_new'
  ): Promise<{ total: number; imported: number; skipped: number; updated: number }> => {
    // Build multipart form-data manually to include file
    const token = await getToken();
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    if (!userId) throw new Error('User authentication required');
    const form = new FormData();
    form.append('file', file);
    form.append('userId', userId);
    form.append('duplicateHandling', duplicateHandling);
    form.append('fieldMapping', JSON.stringify(fieldMapping));
    const options: RequestInit = {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: form
    };
    const url = buildApiUrl('/email/contacts/import');
    return fetchJSON<{ total: number; imported: number; skipped: number; updated: number }>(url, options);
  },

  // Mailbox CRUD + test
  createMailbox: async (mailbox: EmailMailbox): Promise<EmailMailbox> => {
    const { options } = await createRequestOptions('POST', mailbox);
    const url = buildApiUrl('/email/mailboxes');
    const response = await fetchJSON<{ mailbox: EmailMailbox }>(url, options);
    return response.mailbox;
  },
  updateMailbox: async (id: string, mailbox: Partial<EmailMailbox>): Promise<EmailMailbox> => {
    const { options } = await createRequestOptions('PATCH', mailbox);
    const url = buildApiUrl(`/email/mailboxes/${id}`);
    const response = await fetchJSON<{ mailbox: EmailMailbox }>(url, options);
    return response.mailbox;
  },
  deleteMailbox: async (id: string): Promise<void> => {
    const { options } = await createRequestOptions('DELETE');
    const url = buildApiUrl(`/email/mailboxes/${id}`);
    await fetchJSON<void>(url, options);
  },
  testMailboxConnection: async (mailboxData: EmailMailbox): Promise<boolean> => {
    const { options } = await createRequestOptions('POST', mailboxData);
    const url = buildApiUrl('/email/mailboxes/test');
    const res = await fetchJSON<{ success: boolean }>(url, options);
    return !!res?.success;
  },
  
  downloadMailboxTemplate: async (): Promise<void> => {
    const token = await getToken();
    const url = buildApiUrl('/email/mailboxes/template');
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download template');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = 'mailbox_import_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
  },
  
  importMailboxes: async (
    file: File,
    options: { testConnections?: boolean; duplicateHandling?: 'skip' | 'update' }
  ): Promise<MailboxImportResult> => {
    const token = await getToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('testConnections', String(options.testConnections || false));
    formData.append('duplicateHandling', options.duplicateHandling || 'skip');

    const url = buildApiUrl('/email/mailboxes/import');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Import failed' }));
      throw new Error(error.error || 'Import failed');
    }

    return response.json();
  },
  
  // Email Warmup methods
  getMailboxWarmupStatus: async (mailboxId: string): Promise<WarmupStatus | null> => {
    try {
      const { options } = await createRequestOptions('GET');
      const url = buildApiUrl(`/email/warmup/status/${mailboxId}`);
      const response = await fetch(url, options);
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        console.error(`API Error: ${response.status} ${response.statusText}`, { url });
        const errorText = await response.text().catch(() => '');
        if (errorText) {
          console.error('Error response text:', errorText);
        }
        throw new Error(`Network response was not ok: ${response.status} ${response.statusText}`);
      }
      const payload = await response.json() as { status?: WarmupStatus };
      return payload?.status ?? null;
    } catch (error) {
      console.error('Error getting warmup status:', error);
      return null;
    }
  },
  
  enableWarmup: async (mailboxId: string, config?: Partial<WarmupConfig>): Promise<WarmupStatus> => {
    const { options } = await createRequestOptions('POST', {
      isActive: true,
      config
    });
    const url = buildApiUrl(`/email/warmup/status/${mailboxId}`);
    const response = await fetchJSON<{ status: WarmupStatus }>(url, options);
    return response.status;
  },
  
  disableWarmup: async (mailboxId: string): Promise<WarmupStatus> => {
    const { options } = await createRequestOptions('POST', {
      isActive: false
    });
    const url = buildApiUrl(`/email/warmup/status/${mailboxId}`);
    const response = await fetchJSON<{ status: WarmupStatus }>(url, options);
    return response.status;
  },
  
  getWarmupHistory: async (mailboxId: string, page: number = 1, pageSize: number = 20): Promise<WarmupHistoryResponse> => {
    const { options, queryParams } = await createRequestOptions('GET');
    const params = { 
      page: page.toString(),
      pageSize: pageSize.toString(),
      ...queryParams
    };
    const url = buildApiUrl(`/email/warmup/history/${mailboxId}`, params);
    
    return fetchJSON<WarmupHistoryResponse>(url, options);
  },
  
  getWarmupStatistics: async (mailboxId: string): Promise<WarmupStatistics> => {
    const { options } = await createRequestOptions('GET');
    const url = buildApiUrl(`/email/warmup/${mailboxId}/statistics`);
    
    return fetchJSON<WarmupStatistics>(url, options);
  },

  importGoogleSheetsContacts: async (sheetUrl: string, sheetName: string, range: string, fieldMapping: Record<string, string>, duplicateHandling: 'skip' | 'update' | 'create_new'): Promise<{ total: number, imported: number, skipped: number, updated: number }> => {
    try {
      const token = await getToken();
      
      // Get user ID from Supabase for backend authentication
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      
      if (!userId) {
        throw new Error('User authentication required');
      }
      
      const requestBody = {
        userId,
        sheetUrl,
        sheetName,
        range,
        fieldMapping,
        duplicateHandling
      };
      
      const options: RequestInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      };
      
      const response = await fetch(buildApiUrl('email/contacts/import-google-sheets'), options);
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status} ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error importing Google Sheets contacts:', error);
      throw error;
    }
  }
};

export default emailService;

// Note: Types UnifiedThread, UnifiedMessage, UnifiedThreadsResponse, and UnifiedThreadMessagesResponse
// are already exported above via export interface declarations
